"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Panel,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  buildSwot,
  SWOT_QUADRANTS,
  type SwotInput,
  type SwotQuadrant,
} from "@/lib/tool-kit/essay-templates/swot";

/** English strings. A translated content file overrides these through `ui`. */
export const SWOT_ANALYSIS_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  subjectHeading: "What are you analyzing?",
  subjectLabel: "Subject — a business, a project, an organization, or yourself",
  subjectPlaceholder: "e.g. A student-run coffee cart",
  objectiveLabel: "Objective — the decision or goal this analysis supports",
  objectivePlaceholder: "e.g. Decide whether to open a second location",
  quadrantsHeading:
    "The four quadrants — one item per line, most important first",
  quadrantLabels: {
    strengths: "Strengths (internal, helpful)",
    weaknesses: "Weaknesses (internal, harmful)",
    opportunities: "Opportunities (external, helpful)",
    threats: "Threats (external, harmful)",
  },
  quadrantPlaceholders: {
    strengths: "e.g. Loyal regular customers",
    weaknesses: "e.g. Open only on weekday mornings",
    opportunities: "e.g. A new building opening nearby",
    threats: "e.g. Rising ingredient costs",
  },
  towsLabel: "Add TOWS strategy prompts",
  fileName: "swot-analysis",
  document: {
    heading: "Your SWOT analysis",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part of the analysis has your content.",
  },
  defaultTitle: "SWOT Analysis",
  titleWithSubject: "SWOT Analysis: {subject}",
  fields: { objective: "Objective" },
  objectivePrompt:
    "Name the decision or goal this analysis supports. Without one, a SWOT is a list of facts with no way to rank them.",
  rankNote:
    "Rank each list so the items that matter most for the objective come first — the strategy questions pair the top two of each.",
  quadrants: {
    strengths: "Strengths",
    weaknesses: "Weaknesses",
    opportunities: "Opportunities",
    threats: "Threats",
  },
  captions: {
    strengths: "Internal · helpful",
    weaknesses: "Internal · harmful",
    opportunities: "External · helpful",
    threats: "External · harmful",
  },
  quadrantPrompts: {
    strengths:
      "What does it do well? Skills, resources, reputation, and advantages others lack.",
    weaknesses:
      "What holds it back? Gaps in skills, money, time, or processes, and complaints you keep hearing.",
    opportunities:
      "What outside changes could it use? Trends, new markets, funding, or rules that work in its favor.",
    threats:
      "What outside forces could hurt it? Competitors, rising costs, new rules, or shifts in demand.",
  },
  towsHeading: "From Analysis to Strategy: TOWS Prompts",
  towsNote:
    "The TOWS matrix (Weihrich, 1982) turns the grid into strategies by pairing an internal factor with an external one. Answer the questions, then write down the strategies worth pursuing.",
  strategies: {
    so: "SO — use strengths to seize opportunities",
    wo: "WO — use opportunities to overcome weaknesses",
    st: "ST — use strengths to counter threats",
    wt: "WT — minimize weaknesses and avoid threats",
  },
  pairQuestions: {
    so: "How could “{a}” help you make the most of “{b}”?",
    wo: "Could “{b}” help you fix or work around “{a}”?",
    st: "How could “{a}” protect you against “{b}”?",
    wt: "What would limit the damage if “{b}” happens while “{a}” is still true?",
  },
  missingPair: "Add {first} and {second} above to get pairing questions here.",
  strategyPrompt: "Write one or two strategies worth pursuing.",
  example: {
    subject: "Daily Grind, a student-run coffee cart on campus",
    objective:
      "Decide whether to open a second cart in the library next semester",
    strengths:
      "Prices about 30% below the campus café\nVolunteer staff keep labor costs low\nLoyal regulars from the business school\nA popular discount for bringing a reusable cup",
    weaknesses:
      "Open only 8 a.m. to 2 p.m. on weekdays\nCard payments sometimes fail at busy times\nStaff turnover every semester as volunteers graduate",
    opportunities:
      "The library is extending its opening hours during exams\nThe student union offers small-business grants\nGrowing demand for plant-based milk",
    threats:
      "A national coffee chain is opening near the main gate\nRising coffee bean prices\nThe university may tighten food-safety rules for student vendors",
  },
} as const;

const EMPTY: SwotInput = {
  subject: "",
  objective: "",
  strengths: "",
  weaknesses: "",
  opportunities: "",
  threats: "",
  tows: true,
};

type TextKey = "subject" | "objective" | SwotQuadrant;

export default function SwotAnalysisGeneratorWidget() {
  const strings = useToolUi(SWOT_ANALYSIS_GENERATOR_STRINGS);
  const [input, setInput] = useState<SwotInput>(EMPTY);
  const doc = useMemo(() => buildSwot(input, strings), [input, strings]);
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput((previous) => ({
              ...EMPTY,
              ...strings.example,
              tows: previous.tows,
            }))
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.subjectHeading}>
        <TextField
          id="sw-subject"
          label={strings.subjectLabel}
          onChange={update("subject")}
          placeholder={strings.subjectPlaceholder}
          value={input.subject}
        />
        <TextField
          id="sw-objective"
          label={strings.objectiveLabel}
          onChange={update("objective")}
          placeholder={strings.objectivePlaceholder}
          value={input.objective}
        />
      </Panel>

      <Panel title={strings.quadrantsHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          {SWOT_QUADRANTS.map((quadrant) => (
            <TextAreaField
              id={`sw-${quadrant}`}
              key={quadrant}
              label={strings.quadrantLabels[quadrant]}
              onChange={update(quadrant)}
              placeholder={strings.quadrantPlaceholders[quadrant]}
              prose
              rows={5}
              value={input[quadrant]}
            />
          ))}
        </div>
        <Checkbox
          checked={input.tows}
          label={strings.towsLabel}
          onChange={(tows) => setInput((previous) => ({ ...previous, tows }))}
        />
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="sw"
        labels={strings.document}
      />
    </div>
  );
}
